import React from 'react';
import {
  Button,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from '@ideoz/ui';
import { Check, ChevronDown, Loader2, Search } from 'lucide-react';
import {
  fetchGoogleFontFamilies,
  getDisplayedFontSections,
  loadGoogleFontPreview,
} from '../googleFonts';

export type FontFamilyPickerProps = {
  label: string;
  value?: string;
  onChange: (value: string | undefined) => void;
  /** When set, adds a "Same as body" option that clears the value. */
  sameAsBodyLabel?: string;
  className?: string;
};

function FontOptionButton({
  family,
  selected,
  onSelect,
}: {
  family: string;
  selected: boolean;
  onSelect: (family: string) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onMouseEnter={() => loadGoogleFontPreview(family)}
      onClick={() => onSelect(family)}
      className={cn(
        'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50 dark:hover:bg-gray-800',
        selected && 'bg-primary-50 text-primary-700 dark:bg-primary-500/10 dark:text-primary-300',
      )}
    >
      <span style={{ fontFamily: family }}>{family}</span>
      {selected && <Check className="size-4 shrink-0" />}
    </button>
  );
}

export function FontFamilyPicker({
  label,
  value,
  onChange,
  sameAsBodyLabel,
  className,
}: FontFamilyPickerProps): JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [families, setFamilies] = React.useState<string[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    if (value) loadGoogleFontPreview(value);
  }, [value]);

  React.useEffect(() => {
    if (!open || families.length > 0) return;
    setIsLoading(true);
    void fetchGoogleFontFamilies()
      .then(setFamilies)
      .finally(() => setIsLoading(false));
  }, [open, families.length]);

  const sections = getDisplayedFontSections(families, query, value);

  const handleSelect = (family: string | undefined) => {
    if (family) loadGoogleFontPreview(family);
    onChange(family);
    setOpen(false);
    setQuery('');
  };

  const displayValue = value ?? sameAsBodyLabel ?? 'Select a font';

  return (
    <div className={className}>
      <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="mt-2 h-11 w-full justify-between px-3 font-normal"
          >
            <span
              className="truncate text-left"
              style={value ? { fontFamily: value } : undefined}
            >
              {displayValue}
            </span>
            <ChevronDown className="size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
          <div className="border-b border-gray-100 p-2 dark:border-gray-800">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search Google Fonts…"
                className="h-9 pl-8"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto p-1">
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
                <Loader2 className="size-4 animate-spin" />
                Loading fonts…
              </div>
            ) : (
              <>
                {sameAsBodyLabel && !query.trim() && (
                  <button
                    type="button"
                    onClick={() => handleSelect(undefined)}
                    className={cn(
                      'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50 dark:hover:bg-gray-800',
                      !value &&
                        'bg-primary-50 text-primary-700 dark:bg-primary-500/10 dark:text-primary-300',
                    )}
                  >
                    {sameAsBodyLabel}
                    {!value && <Check className="size-4 shrink-0" />}
                  </button>
                )}

                {sections.length === 0 ? (
                  <p className="px-3 py-6 text-center text-sm text-gray-500">No fonts found.</p>
                ) : (
                  sections.map((section) => (
                    <div key={section.id}>
                      <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                        {section.label}
                      </p>
                      {section.fonts.map((family) => (
                        <FontOptionButton
                          key={`${section.id}-${family}`}
                          family={family}
                          selected={value === family}
                          onSelect={handleSelect}
                        />
                      ))}
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default FontFamilyPicker;
